import { useState, useEffect, useCallback } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import {
  getVenueEvents,
  getSubscriptionStatus,
  addSubscription,
  removeSubscription,
  getPollTemplates,
  sendPollFromTemplate,
  getUserChats,
  checkGlobalAdminStatus,
  updateEventOverride
} from '../utils/api.js'
import {
  getTelegramUser,
  formatDateTime,
  getSlotsStatus,
  hapticImpact,
  hapticNotification,
  sendDataToBot,
  openExternalLink
} from '../utils/telegram.js'

function normalizeActivityType(type) {
  return String(type || '').toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_')
}

function sanitizeVenueName(name) {
  const normalized = String(name || '').trim()
  if (!normalized) return ''
  return normalized.toLowerCase() === 'no brand' ? '' : normalized
}

function isMissingBrandVenueId(venueId) {
  if (!String(venueId || '').startsWith('brand:')) return false
  const raw = String(venueId).slice('brand:'.length)
  const separator = raw.indexOf(':')
  if (separator === -1) return false
  const brandPart = decodeURIComponent(raw.slice(separator + 1)).trim().toLowerCase()
  return !brandPart || brandPart === 'no brand' || brandPart === 'no_brand'
}

function getVenueDisplayName(venueName, venueId, activityType) {
  const cleaned = sanitizeVenueName(venueName)
  if (cleaned) return cleaned
  if (isMissingBrandVenueId(venueId)) {
    return String(activityType || '').replace(/_/g, ' ').trim() || 'Venue'
  }
  return decodeURIComponent(venueId)
}

function formatTimeRange(startTime, endTime) {
  if (!startTime) return 'N/A'
  const start = formatDateTime(startTime)
  if (!endTime) return start
  try {
    const end = new Date(endTime).toLocaleString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    })
    return `${start}-${end}`
  } catch {
    return start
  }
}

function hasEventDetailsChanged(previousEvent, nextEvent) {
  if (!previousEvent || !nextEvent) return previousEvent !== nextEvent
  return (
    previousEvent.name !== nextEvent.name ||
    previousEvent.startTime !== nextEvent.startTime ||
    previousEvent.endTime !== nextEvent.endTime ||
    previousEvent.address !== nextEvent.address ||
    previousEvent.price !== nextEvent.price ||
    previousEvent.description !== nextEvent.description ||
    previousEvent.availableSlots !== nextEvent.availableSlots ||
    previousEvent.totalSlots !== nextEvent.totalSlots
  )
}

// Only collector-sourced events (a real upstream time slot) can carry a
// durable admin override; manual events already have their own edit flow.
function isOverridableEvent(event) {
  return Boolean(event?.rawData) && !event.rawData.is_manual && event.rawData.location_id != null
}

function getOverrideTimeSlotId(event) {
  return event?.rawData?.id ?? event?.id
}

function getEventSlotsText(event, activityType) {
  const type = normalizeActivityType(activityType)
  if (type === 'quiz' || type === 'quiz_night') return null
  if (type === 'ice_rink') {
    if (event.totalSlots === null || event.totalSlots === undefined) return null
    return `${event.totalSlots}`
  }
  if (event.availableSlots === null || event.availableSlots === undefined) return null
  return `${event.availableSlots}/${event.totalSlots ?? '?'}`
}

function EventCard({ event, onClick, activityTypeKey }) {
  const slotsStatus = getSlotsStatus(event.availableSlots, event.totalSlots)
  const slotsCls = slotsStatus === 'none' ? 'none' : slotsStatus === 'low' ? 'low' : ''
  const typeKey = normalizeActivityType(event.type)
  const resolvedActivityType = typeKey || activityTypeKey
  const slotsText = getEventSlotsText(event, resolvedActivityType)

  return (
      <div
        className="event-card"
      onClick={() => onClick(event)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          if (e.key === ' ') e.preventDefault()
          onClick(event)
        }
      }}
    >
      <div className="event-header">
        <span className="event-title">{event.name}</span>
        {slotsText && (
          <span className={`event-slots ${slotsCls}`}>
            {resolvedActivityType === 'ice_rink' ? `${slotsText} slots` : slotsText}
          </span>
        )}
      </div>
      {event.startTime && (
        <p className="event-time">🕐 {formatTimeRange(event.startTime, event.endTime)}</p>
      )}
      {event.address && (
        <p className="event-time">📍 {event.address}</p>
      )}
      {event.price && (
        <p className="event-time">💶 {event.price}</p>
      )}
    </div>
  )
}

export default function VenueDetailPage() {
  const { venueId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { venueName, activityType } = location.state || {}
  const displayVenueName = getVenueDisplayName(venueName, venueId, activityType)
  const activityTypeKey = normalizeActivityType(activityType)
  const hideVenueCapacity = activityTypeKey === 'quiz' || activityTypeKey === 'quiz_night'

  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [subscribing, setSubscribing] = useState(false)
  const [subscribed, setSubscribed] = useState(false)
  const [pollTemplates, setPollTemplates] = useState([])
  const [alert, setAlert] = useState(null)
  const [userChats, setUserChats] = useState([])
  const [pollTargetChatId, setPollTargetChatId] = useState(null)
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [eventSubscribed, setEventSubscribed] = useState(false)
  const [eventSubLoading, setEventSubLoading] = useState(false)
  const [isGlobalAdmin, setIsGlobalAdmin] = useState(false)
  const [showOverrideForm, setShowOverrideForm] = useState(false)
  const [overrideForm, setOverrideForm] = useState({ address: '', description: '', price: '' })
  const [savingOverride, setSavingOverride] = useState(false)
  const selectedEventSlotsText = selectedEvent
    ? getEventSlotsText(selectedEvent, activityTypeKey)
    : null

  const telegramUser = getTelegramUser()

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [eventsRes, statusRes] = await Promise.all([
        getVenueEvents(venueId),
        getSubscriptionStatus(venueId)
      ])
      setEvents(eventsRes.data.data || [])
      setSubscribed(statusRes.data.subscribed)
    } catch (err) {
      setAlert({ type: 'error', text: err?.response?.data?.error || 'Failed to load venue data' })
    } finally {
      setLoading(false)
    }
  }, [venueId])

  useEffect(() => { loadData() }, [loadData])

  // Load poll templates (always available)
  useEffect(() => {
    getPollTemplates(venueId)
      .then(res => setPollTemplates(res.data.data || []))
      .catch(() => {})
  }, [venueId])

  useEffect(() => {
    checkGlobalAdminStatus()
      .then(res => setIsGlobalAdmin(res.data?.isAdmin === true))
      .catch(() => setIsGlobalAdmin(false))
  }, [])

  // Load group chats where the user is an admin (for poll target dropdown)
  useEffect(() => {
    getUserChats()
      .then(res => {
        const chats = res.data.data || []
        setUserChats(chats)
        setPollTargetChatId(String(telegramUser.id))
      })
      .catch(() => {
        setPollTargetChatId(String(telegramUser.id))
      })
  }, [telegramUser.id])

  useEffect(() => {
    if (!selectedEvent) return
    const refreshedSelectedEvent = events.find((event) => event.id === selectedEvent.id)
    if (!refreshedSelectedEvent) {
      setSelectedEvent(null)
      return
    }
    if (refreshedSelectedEvent === selectedEvent) return
    if (hasEventDetailsChanged(selectedEvent, refreshedSelectedEvent)) {
      setSelectedEvent(refreshedSelectedEvent)
    }
  }, [events, selectedEvent])

  const handleSubscribe = async () => {
    setSubscribing(true)
    hapticImpact('medium')
    try {
      if (subscribed) {
        await removeSubscription(venueId)
        setSubscribed(false)
        setAlert({ type: 'success', text: 'Unsubscribed from updates' })
        sendDataToBot({ action: 'subscription_removed', venueName: displayVenueName || venueId })
      } else {
        await addSubscription({
          venueId,
          venueName: displayVenueName || venueId,
          activityType: activityType || ''
        })
        setSubscribed(true)
        setAlert({ type: 'success', text: 'Subscribed! You will receive updates automatically.' })
        sendDataToBot({ action: 'subscription_confirmed', venueName: displayVenueName || venueId })
      }
    } catch (err) {
      setAlert({ type: 'error', text: err?.response?.data?.error || 'Action failed' })
    } finally {
      setSubscribing(false)
    }
  }

  const handleSendPoll = async (template, event = null) => {
    if (!event && !selectedEvent && events.length !== 1) {
      setAlert({ type: 'error', text: 'Please select a specific game before sending a poll' })
      return
    }
    const eventData = event || selectedEvent || events[0]
    if (!eventData) {
      setAlert({ type: 'error', text: 'Cannot send poll: event data is required to generate poll title' })
      return
    }
    const targetId = pollTargetChatId || String(telegramUser.id)
    hapticImpact('light')
    try {
      await sendPollFromTemplate(template.id, targetId, eventData)
      const targetLabel =
        String(targetId) === String(telegramUser.id) ? 'My DM' :
          userChats.find(c => String(c.id) === String(targetId))?.title ||
          'chat'
      setAlert({ type: 'success', text: `Poll "${template.title}" sent to ${targetLabel}!` })
      hapticNotification('success')
    } catch (err) {
      setAlert({ type: 'error', text: err?.response?.data?.error || 'Failed to send poll' })
    }
  }

  const openEventDetails = async (event) => {
    setSelectedEvent(event)
    setEventSubLoading(true)
    try {
      const status = await getSubscriptionStatus(
        `event:${event.id}`
      )
      setEventSubscribed(status.data.subscribed)
    } catch {
      setEventSubscribed(false)
    } finally {
      setEventSubLoading(false)
    }
  }

  const closeEventDetails = () => {
    setSelectedEvent(null)
    setShowOverrideForm(false)
  }

  const openOverrideForm = () => {
    setOverrideForm({
      address: selectedEvent.address || '',
      description: selectedEvent.description || '',
      price: selectedEvent.price || ''
    })
    setShowOverrideForm(true)
    hapticImpact('light')
  }

  const handleSaveOverride = async (e) => {
    e.preventDefault()
    if (!selectedEvent) return
    setSavingOverride(true)
    try {
      await updateEventOverride(getOverrideTimeSlotId(selectedEvent), overrideForm)
      setAlert({ type: 'success', text: 'Override saved. It will survive the next collector run.' })
      hapticNotification('success')
      setShowOverrideForm(false)
      await loadData()
    } catch (err) {
      setAlert({ type: 'error', text: err?.response?.data?.error || 'Failed to save override' })
      hapticNotification('error')
    } finally {
      setSavingOverride(false)
    }
  }

  const handleEventSubscribe = async () => {
    if (!selectedEvent) return
    const eventVenueId = `event:${selectedEvent.id}`
    setEventSubLoading(true)
    try {
      if (eventSubscribed) {
        await removeSubscription(eventVenueId)
        setEventSubscribed(false)
        setAlert({ type: 'success', text: 'Unsubscribed from event updates' })
      } else {
        await addSubscription({
          venueId: eventVenueId,
          venueName: selectedEvent.name || eventVenueId,
          activityType: activityType || selectedEvent.type || ''
        })
        setEventSubscribed(true)
        setAlert({ type: 'success', text: 'Subscribed to this event! You will receive updates automatically.' })
      }
    } catch (err) {
      setAlert({ type: 'error', text: err?.response?.data?.error || 'Action failed' })
    } finally {
      setEventSubLoading(false)
    }
  }

  return (
    <div>
      <button
        className="btn btn-secondary btn-sm"
        style={{ marginTop: 16, marginBottom: 8, width: 'auto' }}
        onClick={() => navigate(-1)}
      >
        ← Back
      </button>

      <h2 className="section-title">{displayVenueName || decodeURIComponent(venueId)}</h2>

      {activityType && (
        <span className="badge">{activityType.replace(/_/g, ' ')}</span>
      )}

      {alert && (
        <div className={`alert alert-${alert.type}`} style={{ marginTop: 12 }}>
          {alert.text}
          <button
            style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}
            onClick={() => setAlert(null)}
          >✕</button>
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 8, marginTop: 16, marginBottom: 16 }}>
        <button
          className={`btn ${subscribed ? 'btn-secondary' : 'btn-primary'}`}
          onClick={handleSubscribe}
          disabled={subscribing}
          style={{ flex: 1 }}
        >
          {subscribing ? '⏳' : subscribed ? '🔕 Unsubscribe' : '🔔 Subscribe'}
        </button>
      </div>

      {/* Events list */}
      {loading ? (
        <div className="status-message"><div className="loading-spinner" /></div>
      ) : events.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📭</div>
          <div className="empty-state-title">No upcoming events</div>
          <div className="empty-state-text">Check back soon!</div>
        </div>
      ) : (
        events.map(event => <EventCard key={event.id} event={event} onClick={openEventDetails} activityTypeKey={activityTypeKey} />)
      )}

      {/* Poll templates */}
      {pollTemplates.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h3 className="section-title" style={{ fontSize: 16 }}>📊 Send Poll</h3>
          {userChats.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, color: 'var(--tg-theme-hint-color)', display: 'block', marginBottom: 4 }}>
                Send to:
              </label>
              <select
                className="btn btn-secondary"
                style={{ width: '100%', textAlign: 'left', padding: '8px 12px', fontSize: 14 }}
                value={pollTargetChatId || ''}
                onChange={e => setPollTargetChatId(e.target.value)}
              >
                <option value={String(telegramUser.id)}>📱 My DM</option>
                {userChats.map(chat => (
                  <option key={chat.id} value={String(chat.id)}>
                    👥 {chat.title}
                  </option>
                ))}
              </select>
            </div>
          )}
          {pollTemplates.map(tmpl => (
            <div key={tmpl.id} className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div className="card-title">{tmpl.title}</div>
                <div className="card-subtitle">{tmpl.question}</div>
                <div className="card-subtitle">{tmpl.options.length} options</div>
              </div>
              <button className="btn btn-primary btn-sm" onClick={() => handleSendPoll(tmpl)}>
                Send Poll
              </button>
            </div>
          ))}
        </div>
      )}

      {selectedEvent && (
        <div className="modal-overlay" onClick={closeEventDetails}>
          <div className="modal-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="modal-handle" />
            <h3 className="section-title" style={{ marginTop: 0 }}>{selectedEvent.name || 'Event details'}</h3>
            <p className="event-time">🕐 {formatTimeRange(selectedEvent.startTime, selectedEvent.endTime)}</p>
            {selectedEvent.address && <p className="event-time">📍 {selectedEvent.address}</p>}
            {selectedEvent.price && <p style={{ fontSize: 12, color: 'var(--tg-theme-hint-color)' }}>💶 {selectedEvent.price}</p>}
            {selectedEvent.description && <p style={{ marginTop: 8 }}>{selectedEvent.description}</p>}
            {!hideVenueCapacity && selectedEventSlotsText && (
              <p className="event-time" style={{ marginTop: 8 }}>
                Slots: {selectedEventSlotsText}
              </p>
            )}

            {isGlobalAdmin && isOverridableEvent(selectedEvent) && showOverrideForm && (
              <form onSubmit={handleSaveOverride} style={{ marginTop: 16, borderTop: '1px solid var(--tg-theme-hint-color)', paddingTop: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--tg-theme-hint-color)' }}>
                  ✏️ Edit Details (Admin)
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ display: 'block', fontSize: 12, color: 'var(--tg-theme-hint-color)', marginBottom: 4 }}>Address</label>
                  <input
                    className="form-input"
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--tg-theme-hint-color)', background: 'var(--tg-theme-bg-color)', color: 'var(--tg-theme-text-color)', fontSize: 14, boxSizing: 'border-box' }}
                    value={overrideForm.address}
                    onChange={e => setOverrideForm(f => ({ ...f, address: e.target.value }))}
                    placeholder="123 Main Street, City"
                  />
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ display: 'block', fontSize: 12, color: 'var(--tg-theme-hint-color)', marginBottom: 4 }}>Description</label>
                  <textarea
                    className="form-textarea"
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--tg-theme-hint-color)', background: 'var(--tg-theme-bg-color)', color: 'var(--tg-theme-text-color)', fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }}
                    rows={3}
                    value={overrideForm.description}
                    onChange={e => setOverrideForm(f => ({ ...f, description: e.target.value }))}
                  />
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ display: 'block', fontSize: 12, color: 'var(--tg-theme-hint-color)', marginBottom: 4 }}>Price</label>
                  <input
                    className="form-input"
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--tg-theme-hint-color)', background: 'var(--tg-theme-bg-color)', color: 'var(--tg-theme-text-color)', fontSize: 14, boxSizing: 'border-box' }}
                    value={overrideForm.price}
                    onChange={e => setOverrideForm(f => ({ ...f, price: e.target.value }))}
                    placeholder='e.g. 10 or 10.50 (leave blank to clear)'
                  />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={savingOverride}>
                    {savingOverride ? '⏳ Saving…' : '💾 Save Override'}
                  </button>
                  <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setShowOverrideForm(false)} disabled={savingOverride}>
                    Cancel
                  </button>
                </div>
                <p style={{ fontSize: 11, color: 'var(--tg-theme-hint-color)', marginTop: 8 }}>
                  This override survives the next parser run for this event.
                </p>
              </form>
            )}

            {/* Primary CTA: subscribe to get notifications */}
            <button
              className={`btn ${eventSubscribed ? 'btn-secondary' : 'btn-primary'}`}
              style={{ marginTop: 16 }}
              onClick={handleEventSubscribe}
              disabled={eventSubLoading}
            >
              {eventSubLoading ? '⏳' : eventSubscribed ? '🔕 Unsubscribe Event' : '🔔 Subscribe Event'}
            </button>

            {isGlobalAdmin && isOverridableEvent(selectedEvent) && !showOverrideForm && (
              <button
                className="btn btn-secondary"
                style={{ marginTop: 8 }}
                onClick={openOverrideForm}
              >
                ✏️ Edit Details (Admin)
              </button>
            )}

            {/* Secondary CTA: open original source page externally */}
            {selectedEvent.bookingUrl && (
              <button
                className="btn btn-secondary"
                style={{ marginTop: 8 }}
                onClick={() => openExternalLink(selectedEvent.bookingUrl)}
                aria-label="Open event source page in browser"
              >
                🌐 Open Event Page ↗
              </button>
            )}

            <button className="btn btn-secondary" style={{ marginTop: 8 }} onClick={closeEventDetails}>Close</button>

            {pollTemplates.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <h4 style={{ marginBottom: 0 }}>📊 Send Poll</h4>
                  <button
                    className="btn btn-secondary btn-sm"
                    style={{ width: 'auto' }}
                    onClick={() => navigate('/settings/poll-templates')}
                  >
                    Edit Templates
                  </button>
                </div>
                {userChats.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <select
                      className="btn btn-secondary"
                      style={{ width: '100%', textAlign: 'left', padding: '6px 10px', fontSize: 13 }}
                      value={pollTargetChatId || ''}
                      onChange={e => setPollTargetChatId(e.target.value)}
                    >
                      <option value={String(telegramUser.id)}>📱 My DM</option>
                      {userChats.map(chat => (
                        <option key={chat.id} value={String(chat.id)}>
                          👥 {chat.title}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {pollTemplates.map(tmpl => (
                  <div key={tmpl.id} className="sub-row">
                    <div>
                      <div className="card-title" style={{ fontSize: 14 }}>{tmpl.title}</div>
                      <div className="card-subtitle">{tmpl.question}</div>
                    </div>
                  <button className="btn btn-primary btn-sm" onClick={() => handleSendPoll(tmpl, selectedEvent)}>
                    Send
                  </button>
                </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
